import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { LogIn, UserPlus, Loader2, ArrowLeft } from 'lucide-react';

const GOOGLE_AUTH_URL = `https://demobackend.emergentagent.com/auth/v1/env/login?redirect_url=${encodeURIComponent(window.location.origin + '/auth/callback')}`;

const LoginPage = () => {
  const { user, loading, login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <main className="page-content">
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 72px)' }}>
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
        </div>
      </main>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    let result;
    if (mode === 'login') {
      result = await login(email, password);
    } else {
      if (!name.trim()) { setError('Name is required'); setSubmitting(false); return; }
      result = await register(name, email, password);
    }
    if (!result.success) setError(result.error);
    setSubmitting(false);
  };

  return (
    <main className="page-content" data-testid="login-page">
      <section className="page-header-with-image">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">{mode === 'login' ? 'Welcome Back' : 'Get Started'}</span>
          <h1 className="page-title" style={{ marginTop: '0.5rem' }}>
            {mode === 'login' ? 'Sign In to Your Account' : 'Create Your Account'}
          </h1>
          <p className="page-subtitle">
            {mode === 'login'
              ? 'Access your dashboard to manage your energy account.'
              : 'Register to track your energy savings and manage your account.'}
          </p>
        </div>
      </section>

      <section className="section section-light">
        <div className="container" style={{ maxWidth: 460 }}>
          <Card style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <CardContent className="p-8">
              {/* Google Sign-In Button */}
              <a
                href={GOOGLE_AUTH_URL}
                data-testid="google-login-btn"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
                  width: '100%', padding: '0.75rem 1rem', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-default)', background: 'var(--bg-base)',
                  color: 'var(--navy)', fontFamily: 'var(--font-heading)', fontWeight: 600,
                  fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-base)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </a>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.5rem 0' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border-default)' }}></div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border-default)' }}></div>
              </div>

              <form onSubmit={handleSubmit} data-testid="login-form">
                {error && (
                  <div
                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '0.75rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#DC2626' }}
                    data-testid="auth-error"
                  >
                    {error}
                  </div>
                )}

                {mode === 'register' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.813rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Full Name</label>
                    <Input
                      type="text" value={name} onChange={(e) => setName(e.target.value)} required
                      placeholder="John Smith"
                      data-testid="register-name-input"
                      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--navy)' }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.813rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Email</label>
                  <Input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@example.com"
                    data-testid="login-email-input"
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--navy)' }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.813rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Password</label>
                  <Input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--navy)' }}
                  />
                </div>

                <Button
                  type="submit" className="btn-primary w-full" disabled={submitting}
                  data-testid="login-submit-btn"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin mr-2" /> : mode === 'login' ? <LogIn size={18} className="mr-2" /> : <UserPlus size={18} className="mr-2" />}
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {mode === 'login' ? (
                  <span>Don't have an account?{' '}
                    <button onClick={() => { setMode('register'); setError(''); }} style={{ color: 'var(--teal)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }} data-testid="switch-to-register">
                      Register
                    </button>
                  </span>
                ) : (
                  <span>Already have an account?{' '}
                    <button onClick={() => { setMode('login'); setError(''); }} style={{ color: 'var(--teal)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }} data-testid="switch-to-login">
                      Sign In
                    </button>
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              <ArrowLeft size={16} /> Back to Homepage
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
